/**
 * WHICH LOCALES, AND THE EVIDENCE FOR EACH.
 * ========================================
 *
 * The owner's requirement is *"multi-language based on countries where lockers exist"* (FE-09/FE-10).
 * "Where lockers exist" is a measurable claim, so it was measured rather than assumed, and the
 * numbers are recorded here next to the locale they justify. A locale list nobody can audit is a
 * locale list that quietly acquires and loses entries.
 *
 * ─── HOW THESE NUMBERS WERE OBTAINED ───────────────────────────────────────────────────────────
 *
 * Read-only SOQL against the **dev1 sandbox** (`sf data query --target-org dev1-sandbox`) on
 * 2026-09-03. Nothing was written. Three measures, because they disagree and the disagreement is
 * informative:
 *
 *   `fleetInService`  `SELECT Country_Code__c, COUNT(Id) FROM OpportunityLineItem
 *                      WHERE Kiosk_Status__c = 'InService' GROUP BY Country_Code__c`
 *                     Kiosks currently in service. **This is the primary measure.** Country
 *                     coverage is essentially complete: 7 null out of 56,931 rows (0.01%).
 *
 *   `fleetAll`        The same GROUP BY with no status filter — 81,988 rows. Includes
 *                     decommissioned and never-installed kiosks, so it overstates live footprint.
 *
 *   `interventions180``SELECT Country_Code__c, COUNT(Id) FROM Intervention__c
 *                      WHERE CreatedDate = LAST_N_DAYS:180 GROUP BY Country_Code__c`
 *                     Actual field visits — the closest thing to "where the work is", which is the
 *                     owner's own priority framing. ⚠️ **But 48,832 of 70,334 recent interventions
 *                     (69.4%) carry NO country code at all**, so this measure ranks only the 30.6%
 *                     that happen to be stamped. `Intervention__c.Country_Code__c` is one of the
 *                     six fields the before-save automation derives from `Kiosk_Id__c` (PRD §A-20),
 *                     and on this evidence it derives them for well under half the rows. That is
 *                     why the fleet count, not the intervention count, is the ranking key here.
 *
 * ⚠️ **WHAT THE ORG COULD NOT TELL US: 1P vs 3P.** The claim that NL/PT/LU are "3P-only" (and so
 * should not count) cannot be confirmed from Salesforce. `Access_Point_Type__c` is the only
 * candidate discriminator and it does not discriminate: of 56,931 in-service rows, 52,615 (92.4%)
 * carry the generic value `Hub`, with only `OdinLockers` (1,640), `DobbyLockers` (2,672) and
 * `ODIN_LOCKER` (4) naming a hardware family. So the 1P/3P split rests entirely on
 * `discovery/access/countries-languages.md`, which sources it to a **2024-07** wiki table
 * (`w.amazon.com/bin/view/PARP/3P/Expansion/Expansion_What/`) — roughly two years stale at time of
 * writing. Treat "3P-only" as an unverified inheritance, not a measurement.
 *
 * ─── WHAT THE MEASUREMENT CHANGED ──────────────────────────────────────────────────────────────
 *
 * The working assumption handed to this work was **11 countries, with NL, PT and LU removed as
 * 3P-only**. Measured, that is right about two of the three and wrong about the one that matters:
 *
 *   · **PT does not belong on the excluded list.** 297 in-service kiosks and **299 interventions in
 *     the last 180 days** — more recent field work than AT (37), AU (40) and MX (35) *combined*,
 *     all three of which the 11-country list keeps. Whatever PT's hardware ownership is, technicians
 *     are demonstrably filing work against Portuguese sites at a rate that a locale list cannot
 *     dismiss. Excluding PT while including AT is not defensible on volume.
 *   · **NL is marginal but not zero.** 23 in-service kiosks, 22 recent interventions.
 *   · **LU is effectively zero and needs no locale of its own anyway.** 2 in-service kiosks, 0
 *     recent interventions — and Luxembourg's languages are French and German, both already in
 *     phase 1 for FR and DE. Excluding LU costs nothing because including it would add nothing.
 *   · **AE is the one country that genuinely drops out.** 20 rows, **0 in service**, 0 recent
 *     interventions. This is also the whole of the RTL question — see `RTL_FINDING` below.
 *
 * So: **14 country codes have at least one in-service kiosk**; **13 have recent field work**; and
 * the honest short list is **13 countries, not 11** — the correction should have removed AE and LU,
 * not PT.
 *
 * ─── PHASE 1 ───────────────────────────────────────────────────────────────────────────────────
 *
 * Ranked by language rather than by country (a language, not a country, is what gets translated):
 *
 *   | language | in-service kiosks              | recent interventions        |
 *   |----------|-------------------------------|-----------------------------|
 *   | en       | 30,656  (US+GB+CA+AU)         | 11,635                      |
 *   | es       |  6,063  (ES+MX)               |  2,733                      |
 *   | it       |  6,033  (IT)                  |  3,374                      |
 *   | fr       |  4,912  (FR)                  |  1,703                      |
 *   | ja       |  4,764  (JP)                  |    799                      |
 *   | de       |  4,174  (DE+AT)               |    937                      |
 *   | pt       |    297  (PT)                  |    299                      |
 *   | nl       |     23  (NL)                  |     22                      |
 *
 * **Recommended phase 1: `en` + `es` + `it` + `fr` + `ja` + `de`.** That is 99.4% of the
 * country-stamped in-service fleet and 98.5% of country-stamped recent field work, and it stops
 * exactly where the volume falls off a cliff — the gap between `de` (4,174) and `pt` (297) is 14×,
 * far and away the largest step in the table. This is the "where the work actually is" cut rather
 * than an even spread.
 *
 * It also lands on **six languages, which is precisely what the sibling PRISM app already ships**
 * ("17 countries, 6 languages" — `w.amazon.com/bin/view/Zoewn/TTKPRISMApp/`, via
 * `discovery/access/countries-languages.md`). PRD Phase 2 already plans to *"reuse PRISM's 6
 * translated string bundles before commissioning Panther"*. If those six are the same six, phase 1
 * costs zero translation spend. **That coincidence must be checked with `zoewn`, not assumed** — it
 * is the single highest-value question in this workstream and it is recorded as such below.
 *
 * **Phase 2: `pt-PT`** — the country the prior correction wrongly dropped.
 * **Not planned: `nl-NL`** (23 kiosks) and any LU-specific locale (covered by fr/de).
 */

/** A BCP-47 tag this app is prepared to run in. */
export type Locale =
  | 'en-GB'
  | 'de-DE'
  | 'es-ES'
  | 'fr-FR'
  | 'it-IT'
  | 'ja-JP'
  | 'pt-PT'
  | 'nl-NL'
  | 'en-XA';

/**
 * The source locale. Every string in the app is authored in it, so it needs no catalogue and can
 * never be missing a translation.
 *
 * `en-GB` and not `en-US`: the field vocabulary in the schemas is already British
 * ("colour", "kerb", "metres"), the Qualtrics forms it was ported from were authored in the UK, and
 * `Intl` under `en-GB` gives 24-hour times and DD/MM dates, which is the majority convention across
 * the in-scope countries. US technicians see British spellings; that is a smaller cost than
 * re-authoring the source and it is a translation task (`en-US`) if it ever stops being acceptable.
 */
export const SOURCE_LOCALE: Locale = 'en-GB';

export type LocalePhase = 'source' | 'phase-1' | 'phase-2' | 'not-planned' | 'testing-only';

export type TextDirection = 'ltr' | 'rtl';

export interface CountryEvidence {
  /** ISO-3166-1 alpha-2, exactly as `Country_Code__c` holds it. */
  readonly code: string;
  readonly name: string;
  /** `OpportunityLineItem` rows with `Kiosk_Status__c = 'InService'`. The primary measure. */
  readonly fleetInService: number;
  /** All `OpportunityLineItem` rows, any status. */
  readonly fleetAll: number;
  /** `Intervention__c` rows created in the last 180 days carrying this country code. */
  readonly interventions180: number;
  /** All `Intervention__c` rows carrying this country code. */
  readonly interventionsAll: number;
  /** Which registered locale serves this country. `null` where no locale is planned. */
  readonly locale: Locale | null;
  /** Anything a reader would otherwise have to re-derive. */
  readonly note?: string;
}

/**
 * Every country code observed in either object, with its counts. Ordered by `fleetInService`.
 *
 * Kept complete rather than filtered to the in-scope set: the zero rows are the evidence for
 * *excluding* a country, and deleting them would make the exclusion unfalsifiable.
 */
export const COUNTRY_EVIDENCE: readonly CountryEvidence[] = [
  { code: 'US', name: 'United States',  fleetInService: 25277, fleetAll: 37840, interventions180: 10430, interventionsAll: 36137, locale: 'en-GB', note: 'Sees British spellings from the source locale until an en-US catalogue is commissioned.' },
  { code: 'IT', name: 'Italy',          fleetInService:  6033, fleetAll:  8670, interventions180:  3374, interventionsAll: 13024, locale: 'it-IT' },
  { code: 'ES', name: 'Spain',          fleetInService:  5448, fleetAll:  7001, interventions180:  2698, interventionsAll:  5428, locale: 'es-ES' },
  { code: 'FR', name: 'France',         fleetInService:  4912, fleetAll:  6755, interventions180:  1703, interventionsAll:  3873, locale: 'fr-FR' },
  { code: 'JP', name: 'Japan',          fleetInService:  4764, fleetAll:  5620, interventions180:   799, interventionsAll:  1843, locale: 'ja-JP', note: 'Non-Latin script, LTR. 4,764 kiosks but only 799 recent interventions — the lowest visit-per-kiosk rate in the set, so fleet and work rankings disagree most here.' },
  { code: 'GB', name: 'United Kingdom', fleetInService:  4562, fleetAll:  7375, interventions180:   800, interventionsAll:  1699, locale: 'en-GB' },
  { code: 'DE', name: 'Germany',        fleetInService:  3931, fleetAll:  5269, interventions180:   900, interventionsAll:  1977, locale: 'de-DE' },
  { code: 'MX', name: 'Mexico',         fleetInService:   615, fleetAll:   721, interventions180:    35, interventionsAll:   317, locale: 'es-ES', note: 'Served by es-ES in phase 1. es-MX is a phase-2 regional split if MX field volume grows; at 35 recent interventions it does not justify a second Spanish catalogue.' },
  { code: 'CA', name: 'Canada',         fleetInService:   609, fleetAll:  1091, interventions180:   365, interventionsAll:   917, locale: 'en-GB', note: 'OPEN QUESTION. `OpportunityLineItem.Locale__c` shows en_CA 526 and fr_CA ZERO, which contradicts a bilingual field workforce and Quebec signage practice. Either the org data is incomplete or fr-CA genuinely is not used. Cheap either way: fr-FR is already phase 1, so fr-CA is a regional formatting variant, not a new translation.' },
  { code: 'PT', name: 'Portugal',       fleetInService:   297, fleetAll:   364, interventions180:   299, interventionsAll:   741, locale: 'pt-PT', note: 'THE CORRECTION. Dropped from the 11-country list as "3P-only", but 299 recent interventions is more than AT+AU+MX combined (112), all of which that list keeps. Phase 2, on volume — not excluded.' },
  { code: 'AT', name: 'Austria',        fleetInService:   243, fleetAll:   427, interventions180:    37, interventionsAll:   132, locale: 'de-DE', note: 'German. Served by de-DE; a de-AT catalogue would differ only in formatting. Note `Locale__c` records these as `at_AT`, which is not a valid BCP-47 tag — a data defect worth a ticket, not something to reproduce here.' },
  { code: 'AU', name: 'Australia',      fleetInService:   208, fleetAll:   775, interventions180:    40, interventionsAll:    63, locale: 'en-GB' },
  { code: 'NL', name: 'Netherlands',    fleetInService:    23, fleetAll:    27, interventions180:    22, interventionsAll:    93, locale: 'nl-NL', note: 'Registered but NOT PLANNED. 23 in-service kiosks is 0.04% of the fleet. Listed so the number is on the record if someone proposes Dutch.' },
  { code: 'LU', name: 'Luxembourg',     fleetInService:     2, fleetAll:     3, interventions180:     0, interventionsAll:     4, locale: null, note: 'Correctly excluded, but for the wrong reason: not because it is 3P, but because 2 kiosks and 0 recent visits is nothing — AND because Luxembourg speaks French and German, both already phase 1. Adding LU would add no catalogue.' },
  { code: 'AE', name: 'UAE',            fleetInService:     0, fleetAll:    20, interventions180:     0, interventionsAll:     0, locale: null, note: 'The genuine exclusion, and the entire RTL question. 20 kiosk rows, ZERO in service, ZERO interventions ever. See RTL_FINDING.' },
];

export interface LocaleSpec {
  readonly locale: Locale;
  /** ISO-639-1. Locales sharing this can share a translation if regional wording is acceptable. */
  readonly language: string;
  /** For the language picker. In the language's own words — a picker a user cannot read is useless. */
  readonly endonym: string;
  readonly direction: TextDirection;
  readonly phase: LocalePhase;
  /** Sum of `fleetInService` over the countries this locale serves. The ranking key. */
  readonly fleetInService: number;
  /** Sum of `interventions180` over the countries this locale serves. */
  readonly interventions180: number;
  /** Country codes served. */
  readonly countries: readonly string[];
  readonly note?: string;
}

/**
 * The registry. **Ordered by `fleetInService` descending** — the order the picker renders in, so the
 * locale most technicians need is the one nearest their thumb.
 *
 * Being in this list means "the app has a slot for it", NOT "it is translated". Translation status is
 * a property of the catalogue (`catalog/`), deliberately not of this table, so that registering a
 * locale can never be mistaken for having translated it.
 */
export const LOCALES: readonly LocaleSpec[] = [
  { locale: 'en-GB', language: 'en', endonym: 'English',    direction: 'ltr', phase: 'source',      fleetInService: 30656, interventions180: 11635, countries: ['US', 'GB', 'CA', 'AU'] },
  { locale: 'es-ES', language: 'es', endonym: 'Español',    direction: 'ltr', phase: 'phase-1',     fleetInService:  6063, interventions180:  2733, countries: ['ES', 'MX'] },
  { locale: 'it-IT', language: 'it', endonym: 'Italiano',   direction: 'ltr', phase: 'phase-1',     fleetInService:  6033, interventions180:  3374, countries: ['IT'] },
  { locale: 'fr-FR', language: 'fr', endonym: 'Français',   direction: 'ltr', phase: 'phase-1',     fleetInService:  4912, interventions180:  1703, countries: ['FR'] },
  { locale: 'ja-JP', language: 'ja', endonym: '日本語',       direction: 'ltr', phase: 'phase-1',     fleetInService:  4764, interventions180:   799, countries: ['JP'], note: 'Non-Latin script. Needs a CJK font path; already solved in PRISM, so a solved problem to copy rather than one to invent.' },
  { locale: 'de-DE', language: 'de', endonym: 'Deutsch',    direction: 'ltr', phase: 'phase-1',     fleetInService:  4174, interventions180:   937, countries: ['DE', 'AT'] },
  { locale: 'pt-PT', language: 'pt', endonym: 'Português',  direction: 'ltr', phase: 'phase-2',     fleetInService:   297, interventions180:   299, countries: ['PT'] },
  { locale: 'nl-NL', language: 'nl', endonym: 'Nederlands', direction: 'ltr', phase: 'not-planned', fleetInService:    23, interventions180:    22, countries: ['NL'] },
  {
    locale: 'en-XA', language: 'en', endonym: 'English (pseudo)', direction: 'ltr', phase: 'testing-only',
    fleetInService: 0, interventions180: 0, countries: [],
    note:
      'PSEUDO-LOCALE, NOT A TRANSLATION and not shippable to a technician. Mechanically derived from ' +
      'the English source by `catalog/pseudo.ts`: accented substitution plus a 30% length pad plus ' +
      'delimiters. It exists so the plumbing can be proved end-to-end — lazy chunk loading, hash ' +
      'staleness, a realistically-sized catalogue for bundle measurement, and clipped-layout hunting — ' +
      'WITHOUT anyone machine-translating a safety instruction and shipping it. The `en-XA` tag is the ' +
      'CLDR-reserved pseudo-locale range, so no real user can ever have it as a device language.',
  },
];

const BY_LOCALE = new Map(LOCALES.map((l) => [l.locale, l]));

export function localeSpec(locale: Locale): LocaleSpec {
  const spec = BY_LOCALE.get(locale);
  // Unreachable while `Locale` and `LOCALES` agree; a test pins that they do.
  if (!spec) throw new Error(`locale "${locale}" is not in LOCALES`);
  return spec;
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && BY_LOCALE.has(value as Locale);
}

/** Locales a technician may be offered. Excludes `testing-only`, which must never reach the field. */
export const SELECTABLE_LOCALES: readonly Locale[] = LOCALES.filter((l) => l.phase !== 'testing-only').map(
  (l) => l.locale,
);

/** The set intended to ship translated in phase 1, source locale included. */
export const PHASE_1_LOCALES: readonly Locale[] = LOCALES.filter(
  (l) => l.phase === 'source' || l.phase === 'phase-1',
).map((l) => l.locale);

/**
 * FE-11 (RTL) — RECOMMENDATION: **DO NOT BUILD IT.** Marked NEEDS DECISION; here is the decision
 * this evidence supports.
 *
 * RTL is needed only for Arabic and Hebrew. Across both measured objects, the only country code in
 * either script's region that appears at all is **AE**, with:
 *
 *   · 20 `OpportunityLineItem` rows
 *   · **0** with `Kiosk_Status__c = 'InService'`
 *   · **0** `Intervention__c` rows, in the last 180 days or ever
 *
 * SA, IL and EG do not appear in either object at all. So there is no RTL user, and no RTL kiosk in
 * service, anywhere in the data. Building bidirectional layout now means mirroring every component,
 * auditing every icon and margin, and maintaining it against a UX agent's concurrent edits, to serve
 * zero technicians.
 *
 * **What to do instead, because "defer" should not mean "make it expensive later":** the app already
 * pays nothing for these three habits, and they are what actually makes RTL cheap when it arrives —
 * (1) `dir` is set once on `<html>` from `localeSpec().direction`, never assumed in a component;
 * (2) CSS uses logical properties (`margin-inline-start`, not `margin-left`); (3) no component
 * hard-codes reading order. (1) is implemented in `applyDocumentLocale`. (2) and (3) belong to the
 * UX/accessibility agent's `src/ui/**` and are a review checklist item there, not code here.
 *
 * **The trigger to revisit:** any country code in the Arabic or Hebrew set reaching a non-zero
 * `InService` count. That is a one-line SOQL check, not a judgement call.
 */
export const RTL_FINDING = {
  decision: 'defer' as const,
  needsRtl: false,
  /** Country codes that would force RTL, with what was measured for each. */
  evidence: [
    { code: 'AE', script: 'Arabic', fleetAll: 20, fleetInService: 0, interventions180: 0, interventionsAll: 0 },
    { code: 'SA', script: 'Arabic', fleetAll: 0, fleetInService: 0, interventions180: 0, interventionsAll: 0 },
    { code: 'IL', script: 'Hebrew', fleetAll: 0, fleetInService: 0, interventions180: 0, interventionsAll: 0 },
    { code: 'EG', script: 'Arabic', fleetAll: 0, fleetInService: 0, interventions180: 0, interventionsAll: 0 },
  ],
  revisitWhen: "any Arabic- or Hebrew-script country code reaches a non-zero InService kiosk count",
} as const;

/**
 * Things that must be answered by a human, recorded here rather than in a document nobody opens.
 * Every one of these would change the locale list or the cost of filling it.
 */
export const OPEN_QUESTIONS: readonly string[] = [
  'Which 6 languages does PRISM actually ship? If they are en/es/it/fr/ja/de, phase 1 costs zero translation spend by reusing those bundles (PRD Phase 2 already plans this). Owner: zoewn. HIGHEST VALUE.',
  'Is fr-CA genuinely unused? `OpportunityLineItem.Locale__c` records 526 en_CA and 0 fr_CA against 609 in-service Canadian kiosks, which contradicts both a bilingual workforce and Quebec signage law. Data gap or reality?',
  'Is the "NL/PT/LU are 3P-only" claim still true? It traces to a 2024-07 wiki table and Salesforce cannot confirm it — `Access_Point_Type__c` is 92.4% the generic value `Hub`. PT has 299 recent interventions either way.',
  'Why do 69.4% of recent interventions (48,832 of 70,334) carry no country code? The before-save automation is supposed to derive it from Kiosk_Id__c (PRD §A-20). Until this is fixed, intervention volume cannot rank locales and the fleet count has to stand in.',
  'Do the safety-relevant strings (electrical isolation, anchor torque, kerb clearance) need a named reviewer per locale rather than a general translation vendor? A mistranslated torque figure is a different class of defect from a mistranslated button.',
];
